import React from 'react';
import { Alert, Button, Card, Tag, Typography } from 'antd';
import {
  CommentOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { colors } from '../../../theme';
import './ReportDetailContent.css';
import './HiddenUserDetailContent.css';

const { Title } = Typography;

const SIGNAL_STATUS_MAP = {
  PENDING: { label: 'Pendiente de revisión', color: 'warning' },
  REVIEWED: { label: 'Revisado', color: 'success' },
};

function formatUserFullName(user) {
  if (!user || typeof user !== 'object') return null;
  const fullName = [user.firstName, user.lastName, user.secondLastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  return fullName || null;
}

function formatPersonName(user) {
  if (!user || typeof user !== 'object') return '-';
  return formatUserFullName(user) || user.nickname || user.email || '-';
}

function formatContentType(type) {
  if (type === 'VIDEO') return 'Video';
  if (type === 'COMMENT') return 'Comentario';
  return type || '-';
}

function signalStatusTag(status) {
  const mapped = SIGNAL_STATUS_MAP[status] ?? {
    label: status || '-',
    color: 'default',
  };
  return <Tag color={mapped.color}>{mapped.label}</Tag>;
}

function MetaItem({ label, children }) {
  return (
    <div className="report-detail-meta-item">
      <span className="report-detail-meta-label">{label}</span>
      <div className="report-detail-meta-value">{children}</div>
    </div>
  );
}

function flattenSignals(detail) {
  const hides = Array.isArray(detail?.hides) ? detail.hides : [];
  const rows = [];
  hides.forEach((hide) => {
    const signals = Array.isArray(hide?.signals) ? hide.signals : [];
    signals.forEach((signal) => {
      rows.push({
        ...signal,
        viewer: hide.viewer,
        viewerClientId: hide.viewerClientId,
        hiddenAt: hide.createdAt,
      });
    });
  });
  return rows;
}

function videoPlayUrl(content) {
  return content?.urlVideo || content?.url || null;
}

const HiddenUserDetailContent = ({
  detail,
  reviewingSignalId,
  onPreviewVideo,
  onReviewSignal,
}) => {
  const hiddenClient = detail?.hiddenClient ?? detail?.client ?? null;
  const signals = flattenSignals(detail);
  const hiddenByCount = Number(detail?.hiddenByCount) || 0;
  const pendingSignalCount = Number(detail?.pendingSignalCount) || 0;
  const totalSignalCount = Number(detail?.totalSignalCount) || 0;

  return (
    <div className="report-detail">
      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          Usuario bloqueado
        </Title>
        <div className="hidden-user-person">
          <p className="hidden-user-person-name">
            {formatUserFullName(hiddenClient) ||
              hiddenClient?.nickname ||
              '-'}
          </p>
          <p className="hidden-user-person-line">
            Nickname: {hiddenClient?.nickname || '-'}
          </p>
          <p className="hidden-user-person-line">
            Email: {hiddenClient?.email || '-'}
          </p>
        </div>
      </div>

      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          Resumen
        </Title>
        <div className="hidden-user-summary">
          <div className="hidden-user-summary-card">
            <span className="hidden-user-summary-label">Bloqueado por</span>
            <span className="hidden-user-summary-value">
              {hiddenByCount === 1
                ? '1 usuario'
                : `${hiddenByCount} usuarios`}
            </span>
          </div>
          <div className="hidden-user-summary-card">
            <span className="hidden-user-summary-label">
              Señales pendientes
            </span>
            <span className="hidden-user-summary-value">
              {pendingSignalCount}
            </span>
          </div>
          <div className="hidden-user-summary-card">
            <span className="hidden-user-summary-label">Señales totales</span>
            <span className="hidden-user-summary-value">
              {totalSignalCount}
            </span>
          </div>
        </div>
      </div>

      <div>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          Contenido que originó el bloqueo
        </Title>
        {signals.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message="No hay señales registradas para este usuario."
          />
        ) : (
          <div className="hidden-user-signals">
            {signals.map((signal) => {
              const content = signal.content || {};
              const isComment = signal.contentType === 'COMMENT';
              const isPending = signal.status === 'PENDING';
              const playUrl = !isComment ? videoPlayUrl(content) : null;
              const canPlay = Boolean(playUrl);
              const signalKey = signal.id ?? `${signal.contentType}:${signal.contentId}`;

              const openPreview = () => {
                if (!canPlay || !onPreviewVideo) return;
                onPreviewVideo({
                  urlVideo: playUrl,
                  title: content.title || 'Vista previa',
                });
              };

              return (
                <Card key={signalKey} className="hidden-user-signal-card">
                  {signal.deleted ? (
                    <Alert
                      type="warning"
                      showIcon
                      message="Este contenido ya no está disponible para los usuarios, pero se conserva como evidencia administrativa."
                    />
                  ) : null}

                  <div className="report-detail-split">
                    <div className="report-detail-split__left">
                      {isComment ? (
                        <div>
                          <span className="report-detail-meta-label">
                            Comentario:
                          </span>
                          <div className="report-detail-comment-card">
                            <div className="report-detail-comment-body">
                              <p className="hidden-user-signal-quote">
                                {content.comment
                                  ? `“${content.comment}”`
                                  : '—'}
                              </p>
                            </div>
                          </div>
                          <div style={{ marginTop: 16 }}>
                            <MetaItem label="Comment ID:">
                              {content.id ?? signal.contentId ?? '-'}
                            </MetaItem>
                            <MetaItem label="Video ID:">
                              {content.videoId ?? '-'}
                            </MetaItem>
                          </div>
                        </div>
                      ) : (
                        <div className="report-detail-video">
                          <div
                            className={`report-detail-thumb${canPlay ? ' is-playable' : ''}`}
                            role={canPlay ? 'button' : undefined}
                            tabIndex={canPlay ? 0 : undefined}
                            aria-label={
                              canPlay
                                ? 'Reproducir vista previa del video'
                                : undefined
                            }
                            onClick={openPreview}
                            onKeyDown={(e) => {
                              if (!canPlay) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openPreview();
                              }
                            }}
                          >
                            {content.urlThumbnail ? (
                              <img src={content.urlThumbnail} alt="" />
                            ) : (
                              <div className="report-detail-thumb__placeholder">
                                <VideoCameraOutlined
                                  style={{ color: '#fff', fontSize: 24 }}
                                />
                              </div>
                            )}
                            {canPlay && (
                              <div
                                className="report-detail-thumb__play"
                                aria-hidden
                              >
                                <PlayCircleOutlined
                                  style={{
                                    fontSize: 32,
                                    color: '#fff',
                                    filter:
                                      'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          <div className="report-detail-video-meta">
                            <p className="report-detail-video-title">
                              {content.title || '-'}
                            </p>
                            <p className="report-detail-video-line">
                              Tipo: {formatContentType('VIDEO')}
                            </p>
                            <p className="report-detail-video-line">
                              Video ID: {content.id ?? signal.contentId ?? '-'}
                            </p>
                            <p className="report-detail-video-line">
                              Estado del video: {content.status || '-'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="report-detail-split__right">
                      <MetaItem label="Bloqueado por:">
                        {formatPersonName(signal.viewer)}
                      </MetaItem>
                      <MetaItem label="Fecha del bloqueo:">
                        {signal.hiddenAt
                          ? dayjs(signal.hiddenAt).format('DD/MM/YYYY HH:mm')
                          : '-'}
                      </MetaItem>
                      <MetaItem label="Tipo de contenido:">
                        <span className="report-detail-content-type">
                          {isComment ? (
                            <CommentOutlined
                              style={{ color: colors.primary.main }}
                            />
                          ) : (
                            <VideoCameraOutlined
                              style={{ color: colors.primary.main }}
                            />
                          )}
                          {formatContentType(signal.contentType)}
                        </span>
                      </MetaItem>
                      <MetaItem label="Estado:">
                        {signalStatusTag(signal.status)}
                      </MetaItem>
                    </div>
                  </div>

                  {isPending ? (
                    <div>
                      <Button
                        type="primary"
                        loading={reviewingSignalId === signal.id}
                        disabled={reviewingSignalId != null}
                        onClick={() => onReviewSignal(signal.id)}
                      >
                        Marcar como revisado
                      </Button>
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HiddenUserDetailContent;
